class DeliveryAuth {
  constructor() {
    this.apiBaseUrl = 'https://livgood-pt.herokuapp.com/api/v2/delivery';
    this.tokenKey = 'delivery_auth_token';
    this.userDataKey = 'delivery_user_data';
  }

  /**
   * Login delivery man
   * @param {string} username 
   * @param {string} password 
   * @returns {Promise<{success: boolean, message?: string, data?: any}>}
   */
  async login(username, password) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.status === 'success' && data.token) {
        // Store token and user data
        localStorage.setItem(this.tokenKey, data.token);
        
        if (data.data && data.data.deliveryMan) {
          localStorage.setItem(this.userDataKey, JSON.stringify(data.data.deliveryMan));
        }

        return {
          success: true,
          data: data.data
        };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.message || 'Failed to login. Please check your credentials.'
      };
    }
  }

  /**
   * Update delivery status
   * @param {string} orderNumber 
   * @param {string} status 
   * @param {string} note 
   * @param {string} driverName 
   * @returns {Promise<{success: boolean, message?: string, data?: any}>}
   */
  async updateDeliveryStatus(orderNumber, status, note = '', driverName = '') {
    try {
      const token = this.getToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please login again.');
      }

      const body = {
        orderNumber,
        status
      };

      // Only add note if it's not empty
      if (note && note.trim() !== '') {
        body.note = note.trim();
      }

      // Only add driverName if it's not empty
      if (driverName && driverName.trim() !== '') {
        body.driverName = driverName.trim();
      }

      const response = await fetch(`${this.apiBaseUrl}/update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          this.logout();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.message || 'Failed to update delivery status');
      }

      if (data.status === 'success') {
        return {
          success: true,
          data: data.data
        };
      } else {
        throw new Error(data.message || 'Failed to update delivery status');
      }
    } catch (error) {
      console.error('Update status error:', error);
      return {
        success: false,
        message: error.message || 'Failed to update delivery status. Please try again.'
      };
    }
  }

  /**
   * Bulk update delivery status
   * @param {string} driverName 
   * @param {Array} orders - Array of {orderNumber, status, note}
   * @returns {Promise<{success: boolean, message?: string, data?: any}>}
   */
  async bulkUpdateDeliveryStatus(driverName, orders) {
    try {
      const token = this.getToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please login again.');
      }

      const response = await fetch(`${this.apiBaseUrl}/bulk-update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          driverName,
          orders
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.logout();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.message || 'Failed to bulk update delivery status');
      }

      if (data.status === 'success') {
        return {
          success: true,
          data: data.data
        };
      } else {
        throw new Error(data.message || 'Failed to bulk update delivery status');
      }
    } catch (error) {
      console.error('Bulk update status error:', error);
      return {
        success: false,
        message: error.message || 'Failed to bulk update delivery status. Please try again.'
      };
    }
  }

  /**
   * Download delivery records
   * @param {string} type - 'today', 'yesterday', 'last_week', or 'custom'
   * @param {string} startDate - Required if type is 'custom' (YYYY-MM-DD)
   * @param {string} endDate - Required if type is 'custom' (YYYY-MM-DD)
   * @returns {Promise<{success: boolean, message?: string, data?: any}>}
   */
  async downloadDeliveryRecords(type, startDate = null, endDate = null) {
    try {
      const token = this.getToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please login again.');
      }

      let url = `${this.apiBaseUrl}/download?type=${type}`;
      
      if (type === 'custom' && startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.logout();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.message || 'Failed to download delivery records');
      }

      if (data.status === 'success') {
        return {
          success: true,
          data: data.data?.deliveryRecords || []
        };
      } else {
        throw new Error(data.message || 'Failed to download delivery records');
      }
    } catch (error) {
      console.error('Download delivery records error:', error);
      return {
        success: false,
        message: error.message || 'Failed to download delivery records. Please try again.',
        data: []
      };
    }
  }
  

  /**
   * Search delivery by order number
   * @param {string} orderNumber 
   * @param {string|null} statusFilter - Filter by status: 'delivered', 'not delivered', 'picked up', 'others', or null for all
   * @returns {Promise<{success: boolean, message?: string, data?: any}>}
   */
  async searchDeliveryByOrderNumber(orderNumber, statusFilter = null) {
    try {
      const token = this.getToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please login again.');
      }

      let url = `${this.apiBaseUrl}/search?orderNumber=${encodeURIComponent(orderNumber)}`;
      if (statusFilter) {
        url += `&status=${encodeURIComponent(statusFilter)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.logout();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.message || 'Failed to search delivery records');
      }

      if (data.status === 'success') {
        return {
          success: true,
          data: data.data?.deliveryRecords || []
        };
      } else {
        throw new Error(data.message || 'Failed to search delivery records');
      }
    } catch (error) {
      console.error('Search delivery error:', error);
      return {
        success: false,
        message: error.message || 'Failed to search delivery records. Please try again.',
        data: []
      };
    }
  }

  /**
   * Get orders by date
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<{success: boolean, message?: string, data?: any, date?: string, summary?: any}>}
   */
  async getOrdersByDate(date) {
    try {
      const token = this.getToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please login again.');
      }

      const response = await fetch(`${this.apiBaseUrl}/orders-by-date?date=${encodeURIComponent(date)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch orders by date');
      }

      return {
        success: true,
        date: data.date,
        summary: data.summary,
        data: data.data
      };
    } catch (error) {
      console.error('Get orders by date error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch orders by date. Please try again.'
      };
    }
  }

  /**
   * Get delivery history
   * @param {string|null} statusFilter - Filter by status: 'delivered', 'not delivered', 'picked up', 'others', or null for all
   * @returns {Promise<{success: boolean, message?: string, data?: any}>}
   */
  async getDeliveryHistory(statusFilter = null) {
    try {
      const token = this.getToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please login again.');
      }

      let url = `${this.apiBaseUrl}/my-records`;
      if (statusFilter) {
        url += `?status=${encodeURIComponent(statusFilter)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          this.logout();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.message || 'Failed to fetch delivery history');
      }

      if (data.status === 'success' && data.data && data.data.deliveryRecords) {
        return {
          success: true,
          data: data.data.deliveryRecords
        };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Get history error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch delivery history. Please try again.'
      };
    }
  }

  /**
   * Get stored token
   * @returns {string|null}
   */
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Get stored user data
   * @returns {object|null}
   */
  getUserData() {
    const data = localStorage.getItem(this.userDataKey);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Logout user
   */
  logout() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userDataKey);
  }
}

// Make it available globally
window.DeliveryAuth = DeliveryAuth;
